(() => {
  if (customElements.get('fp-campaign-collection')) return;

  customElements.define('fp-campaign-collection', class extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.revision ||= 0;
      this.navigationKey = location.pathname + location.search;
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-campaign-category], [data-campaign-more]');
        if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const append = link.hasAttribute('data-campaign-more');
        if (append && this.controller) return;
        this.load(this.navigationURL(link.href, !append), append, !append);
      }, { signal: this.lifecycle.signal });
      this.querySelector('[data-campaign-retry]').addEventListener('click', () => {
        if (this.failed) this.load(...this.failed);
      }, { signal: this.lifecycle.signal });
      window.addEventListener('popstate', () => {
        const key = location.pathname + location.search;
        if (key === this.navigationKey) return; // Native anchors only change the hash.
        this.navigationKey = key;
        if ([...this.querySelectorAll('[data-campaign-category]')].some(link => new URL(link.href).pathname === location.pathname)) {
          this.load(new URL(location.href), false, false);
        }
      }, { signal: this.lifecycle.signal });
      this.addEventListener('shopify:block:select', (event) => {
        const link = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
        if (link) this.load(this.navigationURL(link.href, true), false, false);
      }, { signal: this.lifecycle.signal });
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.controller?.abort();
      this.revision++;
      this.lifecycle = null;
    }

    navigationURL(href, resetPage) {
      const url = new URL(href, location.href);
      // Keep the language in the Liquid link and the draft context in navigation.
      for (const key of ['preview_theme_id', 'pb', 'view']) {
        if (new URL(location.href).searchParams.has(key)) url.searchParams.set(key, new URL(location.href).searchParams.get(key));
      }
      if (window.Shopify?.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
      if (resetPage) url.searchParams.delete('page');
      return url;
    }

    async load(url, append, pushHistory) {
      this.controller?.abort();
      const controller = this.controller = new AbortController();
      const revision = ++this.revision;
      const status = this.querySelector('[data-campaign-status]');
      const recovery = this.querySelector('[data-campaign-recovery]');
      const current = this.querySelector('[data-campaign-results]');
      const more = this.querySelector('[data-campaign-more]');
      const requestURL = new URL(url);
      requestURL.searchParams.delete('sections');
      // Preview is already established by the document cookie. Shopify redirects
      // section requests with preview_theme_id and can lose section_id/page.
      requestURL.searchParams.delete('preview_theme_id');
      requestURL.searchParams.set('section_id', this.dataset.sectionId);
      current.setAttribute('aria-busy', 'true');
      more?.setAttribute('aria-disabled', 'true');
      status.textContent = this.dataset.loading;
      status.classList.add('visually-hidden');
      recovery.hidden = true;
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(requestURL, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        if (!this.isConnected || revision !== this.revision) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const shelf = parsed.getElementById(`shopify-section-${this.dataset.sectionId}`)?.querySelector('fp-campaign-collection');
        const results = shelf?.querySelector('[data-campaign-results]');
        const grid = results?.querySelector('[data-campaign-grid]');
        const expectedPage = Number(url.searchParams.get('page') || 1);
        if (!shelf || shelf.dataset.themeId !== this.dataset.themeId || !grid || !results.querySelector('[data-campaign-count]') ||
            !results.querySelector('[data-campaign-pagination]') || html.includes('Liquid error') ||
            new URL(results.dataset.collectionUrl, location.href).pathname !== url.pathname ||
            Number(results.dataset.page) !== expectedPage || grid.children.length > 8 ||
            (grid.children.length === 0 && results.dataset.total !== '0')) throw new Error('Invalid campaign section');
        if (append) {
          if (current.dataset.collectionUrl !== results.dataset.collectionUrl || expectedPage !== Number(current.dataset.page) + 1) throw new Error('Invalid next page');
          const ids = new Set([...current.querySelectorAll('[data-product-id]')].map(card => card.dataset.productId));
          if ([...grid.children].some(card => ids.has(card.dataset.productId))) throw new Error('Duplicate product');
          const firstLink = grid.querySelector('a');
          current.querySelector('[data-campaign-grid]').append(...grid.children);
          current.querySelector('[data-campaign-pagination]').replaceWith(results.querySelector('[data-campaign-pagination]'));
          current.dataset.page = results.dataset.page;
          firstLink?.focus({ preventScroll: true });
        } else {
          current.replaceWith(results);
          for (const link of this.querySelectorAll('[data-campaign-category]')) {
            if (new URL(link.href).pathname === url.pathname) link.setAttribute('aria-current', 'page');
            else link.removeAttribute('aria-current');
          }
          if (pushHistory) {
            history.pushState({}, '', url);
            this.navigationKey = location.pathname + location.search;
          }
        }
        status.textContent = results.querySelector('[data-campaign-count]').textContent;
        this.failed = null;
        if (typeof window.refreshFPTrustvoxRatings === 'function') window.refreshFPTrustvoxRatings();
      } catch (error) {
        if (!this.isConnected || revision !== this.revision) return;
        status.textContent = this.dataset.error;
        status.classList.remove('visually-hidden');
        this.failed = [url, append, pushHistory];
        this.querySelector('[data-campaign-fallback]').href = url.href;
        recovery.hidden = false;
      } finally {
        clearTimeout(timeout);
        if (revision === this.revision) {
          this.controller = null;
          this.querySelector('[data-campaign-results]').removeAttribute('aria-busy');
          this.querySelector('[data-campaign-more]')?.removeAttribute('aria-disabled');
        }
      }
    }
  });
})();
